import { useHead } from '@unhead/react';
import { message } from 'antd';
import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { ACTIVITY_TYPES } from 'business/shift-report/activity/types';
import { usePreviousAndNextReportIds } from 'business/shift-report/report/hooks/use-previous-and-next-report-id';
import { parseTaskDateString } from 'business/shift-report/task/services/timeOperations';
import config from 'config';
import Routes from 'config/routes';
import {
  Module_Enum,
  useGetIndicatorResultsQuery,
  useGetPreviousPrintReportQuery,
  useGetPreviousPrintTasksQuery,
  useGetShiftReportPdfMetadataQuery,
} from 'generated/graphql';
import logger from 'technical/logger';
import { useMediaType } from 'technical/media/hooks';
import { NotFound } from 'technical/router/switch/reporting-no-match';
import AppLogo from 'ui/appLogo';
import Loader from 'ui/loader';
import { useShowMore } from 'ui/show-more-button/hook';

import { AvailabilityGraphContainer } from './components/availability-graph-container';
import { ControlsContainer } from './components/controls-container';
import { InProgressReport } from './components/in-progress-report';
import { ReportViewMetadataContainer } from './components/metadata-container';
import { NoteContainer } from './components/notes-container';
import { ReportChartContainer } from './components/report-chart-container';
import { TasksFilesContainer } from './components/tasks-files-container/tasks-files-container';
import { TasksTableContainer } from './components/tasks-table-container';
import { ReportViewMobile } from './mobile-page';
import {
  averageRingTimeWithTypeSelection,
  getFinishedRingFromTaskList,
} from './services';
import { ShowMoreOnMobileState } from './types';

import './index.scss';

const useBackRoute = () => {
  const {
    modules: {
      SHIFT_REPORT: { hasManagerReadPermission },
    },
  } = usePermissions();

  const [searchParams] = useSearchParams();

  let route = searchParams.get('redirect') ?? Routes.ReportList;
  if (hasManagerReadPermission) {
    route = searchParams.get('redirect') ?? Routes.ReportListValidated;
  }

  const filters = searchParams.get('filters');

  return {
    redirect: route,
    filters,
  };
};

function ReportViewPage() {
  const { t } = useTranslation();
  const { isMobile } = useMediaType();

  useHead({ title: t('user.menu.sections.reporting.title') });

  const { currentConstructionSite, constructionSites } = useAppContext();
  const {
    modules: {
      SHIFT_REPORT: { hasManagerEditPermission },
    },
  } = usePermissions();
  const { id: shiftReportId } = useParams<{ id: string }>();
  invariant(shiftReportId, "id isn't set within the route");

  const gqlHeaders = useGraphqlApiHeaders(Module_Enum.ShiftReport);

  const { showMore, toggleShowMore } = useShowMore<ShowMoreOnMobileState>({
    metadata: false,
    tasks: false,
    notes: false,
    graph: false,
    files: false,
  });

  const { redirect, filters } = useBackRoute();

  const { previousReportId, nextReportId } = usePreviousAndNextReportIds(
    redirect,
    filters,
    shiftReportId,
    currentConstructionSite?.id,
  );

  const { loading, error, data } = useGetShiftReportPdfMetadataQuery({
    variables: {
      shiftReportId,
      constructionSiteId: currentConstructionSite?.id,
    },
    skip: !shiftReportId || !currentConstructionSite?.id,
    fetchPolicy: 'cache-and-network',
    context: {
      headers: gqlHeaders,
    },
  });

  const report = data?.shiftReport ? data.shiftReport : undefined;
  const tasksForAverageExcavation = data?.tasksForAverageExcavation
    ? data.tasksForAverageExcavation
    : undefined;
  const tasksForAverageBuild = data?.tasksForAverageBuild
    ? data.tasksForAverageBuild
    : undefined;
  const endedExcavationRings = getFinishedRingFromTaskList(
    tasksForAverageExcavation,
  );
  const endedBuildRings = getFinishedRingFromTaskList(tasksForAverageBuild);
  const nbRingsBuild = endedBuildRings.length;

  const reportName = report?.constructionSite.name;
  const logo = report?.constructionSite.logo;

  const {
    loading: loadingPreviousReport,
    error: errorPreviousReport,
    data: dataPreviousReport,
  } = useGetPreviousPrintReportQuery({
    variables: {
      constructionSiteId: report?.constructionSite?.id,
      date: report?.date,
      shiftReportId: report?.id,
      shiftStartTime: report?.shift.startTime,
    },
    skip: !report,
    context: {
      headers: gqlHeaders,
    },
  });

  const previousReport = dataPreviousReport?.shiftReport
    ? dataPreviousReport.shiftReport[0]
    : undefined;

  const {
    loading: loadingPreviousTasks,
    error: errorPreviousTasks,
    data: dataPreviousTasks,
  } = useGetPreviousPrintTasksQuery({
    variables: {
      constructionSiteId: report?.constructionSite?.id,
      shiftReportId,
      firstEndedExcavationRing: endedExcavationRings[0],
      firstEndedBuildRing: endedBuildRings[0],
      withExcavationTasks: !!endedExcavationRings[0],
      withBuildTasks: !!endedBuildRings[0],
    },
    skip: !report,
    context: {
      headers: gqlHeaders,
    },
  });

  const {
    loading: loadingIndicatorResults,
    error: errorIndicatorResults,
    data: dataIndicatorResults,
  } = useGetIndicatorResultsQuery({
    variables: {
      shiftReportId,
      constructionSiteId: report?.constructionSite?.id,
    },
    skip: !report,
    fetchPolicy: 'network-only',
    context: {
      headers: gqlHeaders,
    },
  });

  useEffect(function alertUser() {
    if (error) {
      message.error(t('errors.error_generic'));
      logger.error(error);
    }
    if (errorPreviousReport) {
      message.error(t('errors.error_generic'));
      logger.error(errorPreviousReport);
    }
    if (errorPreviousTasks) {
      message.error(t('errors.error_generic'));
      logger.error(errorPreviousTasks);
    }
    if (errorIndicatorResults) {
      message.error(t('errors.error_generic'));
      logger.error(errorIndicatorResults);
    }
    // Only trigger alert once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (
    !loading &&
    !report?.constructionSite &&
    !constructionSites.find(({ id }) => id === report?.constructionSite.id)
  ) {
    return <NotFound />;
  }

  if (
    loading ||
    loadingPreviousReport ||
    loadingPreviousTasks ||
    loadingIndicatorResults
  ) {
    return (
      <div className="report-view-page page-appear">
        <Loader />
      </div>
    );
  }

  const averageRingTimePerType = averageRingTimeWithTypeSelection(
    endedExcavationRings,
    endedBuildRings,
    tasksForAverageExcavation,
    tasksForAverageBuild,
    dataPreviousTasks,
  );

  const averageExcavationPerRing = averageRingTimePerType(
    ACTIVITY_TYPES.EXCAVATION,
  );
  const averageBuildPerRing = averageRingTimePerType(ACTIVITY_TYPES.BUILD);
  const indicators = dataIndicatorResults?.calculateIndicatorResults?.data;

  let totalTasksTime: Duration = dayjs.duration(0, 'minutes');
  let totalCriticalTasksTime: Duration = dayjs.duration(0, 'minutes');

  if (report) {
    totalTasksTime = totalTasksTime.add(
      report.tasks.reduce((acc, task) => {
        return (
          acc +
          (task.duration ||
            parseTaskDateString(task.endDate).diff(
              parseTaskDateString(task.startDate),
              'minutes',
            ))
        );
      }, 0),
      'minutes',
    );

    totalCriticalTasksTime = totalCriticalTasksTime.add(
      report.tasks.reduce((acc, task) => {
        if (task.critical) {
          return (
            acc +
            (task.duration ||
              parseTaskDateString(task.endDate).diff(
                parseTaskDateString(task.startDate),
                'minutes',
              ))
          );
        }

        return acc;
      }, 0),
      'minutes',
    );
  }

  if (isMobile) {
    return (
      <>
        {data && dataPreviousReport && report && (
          <ReportViewMobile
            report={report}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
            previousReport={previousReport}
            averageExcavationPerRing={averageExcavationPerRing}
            averageBuildPerRing={averageBuildPerRing}
            indicators={indicators}
            nbRingsBuild={nbRingsBuild}
            activities={data.activities}
            totalTasksTime={totalTasksTime}
            totalCriticalTasksTime={totalCriticalTasksTime}
            currentConstructionSite={currentConstructionSite}
            hasManagerEditPermission={hasManagerEditPermission}
            redirect={redirect}
            filters={filters}
            previousReportId={previousReportId}
            nextReportId={nextReportId}
          />
        )}
      </>
    );
  }

  return (
    <div className="report-view-page page-appear">
      {data && dataPreviousReport && report && (
        <>
          <InProgressReport operatorValidation={report.operatorValidation} />

          <ControlsContainer
            redirect={redirect}
            filters={filters}
            currentConstructionSite={currentConstructionSite}
            hasManagerEditPermission={hasManagerEditPermission}
            report={report}
            previousReportId={previousReportId}
            nextReportId={nextReportId}
          />

          <div className="header">
            <div className="header-left">
              <AppLogo />
              {logo && (
                <AppLogo
                  alt="Construction site logo"
                  path={`${config.gcp.publicUri}/${logo}`}
                />
              )}
            </div>
            <h1>{t('pages.print.mainTitle', { reportName })}</h1>
          </div>

          <ReportViewMetadataContainer
            report={report}
            previousReport={previousReport}
            averageExcavationPerRing={averageExcavationPerRing}
            averageBuildPerRing={averageBuildPerRing}
            indicators={indicators}
            totalTasksTime={totalTasksTime}
            totalCriticalTasksTime={totalCriticalTasksTime}
          />

          <ReportChartContainer
            report={report}
            nbRingsBuild={nbRingsBuild}
            activities={data.activities}
          />

          <TasksTableContainer
            showMore={showMore}
            toggleShowMore={toggleShowMore}
            activities={data.activities}
            tasks={report.tasks}
          />

          <NoteContainer
            note={report.note}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />

          <AvailabilityGraphContainer
            report={report}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />

          <TasksFilesContainer
            tasks={report.tasks}
            showMore={showMore}
            toggleShowMore={toggleShowMore}
          />
        </>
      )}
    </div>
  );
}

export default ReportViewPage;
